import { apiWithETag, type APIWithETagResult } from '../../lib/api';
import type { UserRole } from '../../types/permissions';
import type { Pedido } from '../../types/orders';
import { extractOrcList, regroupFlatItems } from './orderNormalizer';
import { OrderPageCache } from './orderPageCache';

const COMMERCIAL_STATUSES = [1, 2, 3] as const;
const COMMERCIAL_LIMITS = [10, 50, 100] as const;
const PRODUCTION_STATUSES = [1, 2, 3, 6] as const;
const PRODUCTION_LIMIT = 64;
const PRODUCTION_ORDER_TYPE = 3;
const MAX_PAGES_PER_STATUS = 1_000;
const DEFAULT_ETAG_TTL_MS = 60_000;

type OrderHttpClient = (
  endpoint: string,
  options?: RequestInit,
  opts?: { ifNoneMatch?: string; authTokenOverride?: string },
) => Promise<APIWithETagResult>;

type OrderNormalizer = (orders: unknown[], role: UserRole) => Pedido[];

export interface OrderQueryScope {
  tenantKey: string;
  role: UserRole;
  userId?: number;
}

export interface FetchOrdersOptions {
  cacheMode?: 'validate' | 'bypass';
}

interface OrderQueryServiceOptions {
  scope: OrderQueryScope;
  httpClient?: OrderHttpClient;
  normalizeOrders: OrderNormalizer;
  now?: () => number;
  etagTtlMs?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getNestedRecord(
  record: Record<string, unknown>,
  key: string,
): Record<string, unknown> {
  return isRecord(record[key]) ? record[key] : {};
}

function mergeRawOrders(target: Map<string, unknown>, orders: unknown[]): void {
  for (const value of orders) {
    if (!isRecord(value)) continue;
    const orderId = value.orc_id ?? getNestedRecord(value, 'orc').id ?? value.id;
    if (orderId == null) continue;

    const key = String(orderId);
    const previous = target.get(key);
    if (!isRecord(previous)) {
      target.set(key, value);
      continue;
    }

    const previousItems = Array.isArray(previous.itens) ? previous.itens : [];
    const nextItems = Array.isArray(value.itens) ? value.itens : [];
    const itemsById = new Map<string, unknown>();

    for (const item of [...previousItems, ...nextItems]) {
      if (!isRecord(item) || item.oit_id == null) continue;
      itemsById.set(String(item.oit_id), item);
    }

    target.set(key, {
      ...previous,
      ...value,
      itens: Array.from(itemsById.values()),
    });
  }
}

function getPageCount(response: unknown, collection: unknown[]): number {
  if (!isRecord(response)) return collection.length;
  const payload = isRecord(response.data) ? response.data : response;
  if (Array.isArray(payload.items_flat)) return payload.items_flat.length;
  return collection.length;
}

function isHttpStatus(error: unknown, status: number): boolean {
  return isRecord(error) && error.status === status;
}

export class OrderQueryService {
  private readonly scope: OrderQueryScope;
  private readonly httpClient: OrderHttpClient;
  private readonly normalizeOrders: OrderNormalizer;
  private readonly pageCache: OrderPageCache;

  constructor(options: OrderQueryServiceOptions) {
    this.scope = options.scope;
    this.httpClient = options.httpClient ?? apiWithETag;
    this.normalizeOrders = options.normalizeOrders;
    this.pageCache = new OrderPageCache({
      scopeKey: [
        options.scope.tenantKey,
        options.scope.role,
        options.scope.userId ?? 'anonymous',
      ].join(':'),
      ttlMs: options.etagTtlMs ?? DEFAULT_ETAG_TTL_MS,
      now: options.now,
    });
  }

  async fetchOrders(options: FetchOrdersOptions = {}): Promise<Pedido[]> {
    const cacheMode = options.cacheMode ?? 'validate';
    if (cacheMode === 'bypass') this.pageCache.clear();

    const rawOrders =
      this.scope.role === 'producao'
        ? await this.fetchProductionOrders(cacheMode)
        : await this.fetchCommercialOrders(cacheMode);

    return this.normalizeOrders(rawOrders, this.scope.role);
  }

  clearCache(): void {
    this.pageCache.clear();
  }

  private async fetchCommercialOrders(
    cacheMode: 'validate' | 'bypass',
  ): Promise<unknown[]> {
    const userId = this.scope.userId;
    if (!userId || userId <= 0) {
      throw new Error('Usuário não resolvido para consultar pedidos');
    }

    const orders = new Map<string, unknown>();
    let fetchedAny = false;

    for (const limit of COMMERCIAL_LIMITS) {
      for (const status of COMMERCIAL_STATUSES) {
        let page = 0;

        while (page < MAX_PAGES_PER_STATUS) {
          const endpoint =
            `/orcamento-pedido-status/${limit}/${page}` +
            `?orcstatus=${status}&searchItem=&id=${encodeURIComponent(String(userId))}` +
            '&mode=cdr';
          const response = await this.fetchPage(endpoint, cacheMode);
          const collection = extractOrcList(response);
          mergeRawOrders(orders, collection);

          if (collection.length > 0) fetchedAny = true;
          if (collection.length < limit) break;
          page += 1;
        }
      }

      if (fetchedAny) break;
    }

    return Array.from(orders.values());
  }

  private async fetchProductionOrders(
    cacheMode: 'validate' | 'bypass',
  ): Promise<unknown[]> {
    const statusResults = await Promise.all(
      PRODUCTION_STATUSES.map((status) =>
        this.fetchProductionStatus(status, cacheMode),
      ),
    );
    const orders = new Map<string, unknown>();

    for (const statusOrders of statusResults) {
      mergeRawOrders(orders, statusOrders);
    }

    return Array.from(orders.values());
  }

  private async fetchProductionStatus(
    status: number,
    cacheMode: 'validate' | 'bypass',
  ): Promise<unknown[]> {
    const results: unknown[] = [];
    let page = 0;

    while (page < MAX_PAGES_PER_STATUS) {
      const endpoint =
        `/orcamento_itens_prod/${PRODUCTION_LIMIT}/${page}/${status}` +
        `/${PRODUCTION_ORDER_TYPE}?mode=cdr&_v=fix_tags_v5`;

      try {
        const response = await this.fetchPage(endpoint, cacheMode);
        const collection = extractOrcList(response);
        const hasFlatItems = collection.some(
          (value) =>
            isRecord(value) &&
            value.oit_id != null &&
            isRecord(value.orcamento),
        );
        if (hasFlatItems) {
          results.push(...regroupFlatItems(collection, status));
        } else {
          for (const value of collection) {
            if (!isRecord(value)) continue;
            const items = Array.isArray(value.itens)
              ? value.itens.map((item) =>
                  isRecord(item)
                    ? {
                        ...item,
                        __etapa_producao__:
                          getNestedRecord(item, 'status').oit_status_id ?? status,
                      }
                    : item,
                )
              : [];
            results.push({
              ...value,
              __etapa_producao__: status,
              itens: items,
            });
          }
        }

        if (getPageCount(response, collection) === 0) break;
        page += 1;
      } catch (error) {
        if (isHttpStatus(error, 404)) break;
        throw error;
      }
    }

    if (page >= MAX_PAGES_PER_STATUS) {
      throw new Error(
        `Limite defensivo de paginação atingido para o status ${status}`,
      );
    }

    return results;
  }

  private async fetchPage(
    endpoint: string,
    cacheMode: 'validate' | 'bypass',
  ): Promise<unknown> {
    if (cacheMode === 'bypass') this.pageCache.clearPage(endpoint);

    const ifNoneMatch =
      cacheMode === 'validate'
        ? this.pageCache.getFreshEtag(endpoint)
        : undefined;
    const result = await this.httpClient(
      endpoint,
      { method: 'GET' },
      { ifNoneMatch },
    );

    if (result.notModified) {
      const cached = this.pageCache.getData(endpoint);
      if (cached !== undefined) return cached;

      const retry = await this.httpClient(
        endpoint,
        { method: 'GET' },
        { ifNoneMatch: undefined },
      );
      if (retry.notModified) {
        throw new Error(`Resposta 304 sem corpo em cache para ${endpoint}`);
      }
      this.pageCache.set(endpoint, {
        data: retry.data,
        etag: retry.etag,
      });
      return retry.data;
    }

    this.pageCache.set(endpoint, {
      data: result.data,
      etag: result.etag,
    });
    return result.data;
  }
}
